// frontend/src/scenes/admin/index.jsx
import React from "react";
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BusinessIcon from "@mui/icons-material/Business";
import ScienceIcon from "@mui/icons-material/Science";
import SettingsIcon from "@mui/icons-material/Settings";
import TableChartIcon from "@mui/icons-material/TableChart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { getUser } from "../../lib/authApi";

function Tile({ title, subtitle, icon, to }) {
  const navigate = useNavigate();
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardActionArea onClick={() => navigate(to)} sx={{ height: "100%" }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            {icon}
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function Admin() {
  const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
  const canViewAuditLogs = ["owner", "global_admin", "global_reader", "superadmin"].includes(role);

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Header title="Admin" subtitle="Manage organizations, users, and reference data" />

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* ✅ Org / User management */}
        <Grid item xs={12} md={6} lg={4}>
          <Tile
            title="Organizations"
            subtitle="Create and select lab organizations"
            icon={<BusinessIcon />}
            to="/admin/organizations"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile
            title="Accounts"
            subtitle="Manage LIS accounts, locations, billing, reports, and assigned resources"
            icon={<BusinessIcon />}
            to="/admin/accounts"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile
            title="Report Settings"
            subtitle="Configure report layout, disclaimers, footer text, and PDF options"
            icon={<AdminPanelSettingsIcon />}
            to="/admin/report-settings"
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile
            title="Departments"
            subtitle="Set up departments and default result labels; analytes are managed on the connected Analytes page"
            icon={<ScienceIcon />}
            to="/admin/departments"
          />
        </Grid>

        {canViewAuditLogs ? (
          <Grid item xs={12} md={6} lg={4}>
            <Tile
              title="Audit Logs"
              subtitle="Global security/activity log for logins, admin changes, reports, and PHI-related activity"
              icon={<ManageSearchIcon />}
              to="/admin/audit-logs"
            />
          </Grid>
        ) : null}

        {/* Existing tiles */}
        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Lab Results" subtitle="Review panels, import TOX CSV, generate reports" icon={<ScienceIcon />} to="/lab-results" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Analytes" subtitle="View and add analytes by organization and department" icon={<TableChartIcon />} to="/analytes" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Patients" subtitle="Patient directory" icon={<PeopleAltIcon />} to="/patients" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Providers" subtitle="Provider directory (NPI, names)" icon={<LocalHospitalIcon />} to="/providers" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Facilities" subtitle="Facilities directory" icon={<BusinessIcon />} to="/facilities" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Lab Settings" subtitle="Org-level lab settings" icon={<SettingsIcon />} to="/lab-settings" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Users" subtitle="Create users and assign organization access" icon={<PeopleAltIcon />} to="/admin/users" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Invites" subtitle="Invite users to orgs" icon={<PeopleAltIcon />} to="/invite" />
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Tile title="Azure Settings" subtitle="Azure OAuth / app settings" icon={<SettingsIcon />} to="/azure-settings" />
        </Grid>
      </Grid>
    </Box>
  );
}